import {
  ArrowRight,
  Gift,
  GitBranch,
  MessageSquare,
  Package,
  Send,
  StickyNote,
  Tag,
  Truck,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";

import { cn } from "@/marketing/lib/utils";
import {
  CampaignActivityAction,
  type CampaignActivityLog,
  CampaignActivityKind,
} from "@/marketing/models/campaignActivity";
import { CAMPAIGN_ACTIVITY_QUERIES } from "@/marketing/infrastructure/influencer/campaign/activity/utils/queries";
import { formatDate } from "@/marketing/lib/formatters";
import { getActivityAuthorLabel } from "@/marketing/lib/activityAuthor";
import { getStageTransitionCauseLabel } from "@/marketing/lib/stageTransitionCause";

interface ActivityTabProps {
  campaignId: string;
}

interface IconProps {
  className?: string;
}

const ACTION_LABELS: Record<CampaignActivityAction, string> = {
  [CampaignActivityAction.StageTransition]: "moved stage",
  [CampaignActivityAction.ShipmentSet]: "set shipment tracking",
  [CampaignActivityAction.ContentUrlSet]: "set content URLs",
  [CampaignActivityAction.TagsSet]: "set tags",
  [CampaignActivityAction.ProductSelectionSet]: "assigned products",
  [CampaignActivityAction.RejectedSet]: "rejected",
  [CampaignActivityAction.Unrejected]: "restored from rejected",
  [CampaignActivityAction.CreatorsAdded]: "enrolled creators",
  [CampaignActivityAction.SettlementMarked]: "marked settled",
  [CampaignActivityAction.SettlementUnmarked]: "unmarked settled",
  [CampaignActivityAction.SettlementMetaUpdated]: "updated settlement amount",
};

function ActivityIcon({ entry }: { entry: CampaignActivityLog }) {
  const baseProps: IconProps = { className: "tw-h-3.5 tw-w-3.5" };
  if (entry.kind === CampaignActivityKind.Comment) {
    return (
      <span className="tw-inline-flex tw-h-7 tw-w-7 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-blue-100 tw-text-blue-700">
        <MessageSquare {...baseProps} />
      </span>
    );
  }
  switch (entry.action) {
    case CampaignActivityAction.StageTransition:
      return (
        <span className="tw-inline-flex tw-h-7 tw-w-7 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-muted tw-text-muted-foreground">
          <GitBranch {...baseProps} />
        </span>
      );
    case CampaignActivityAction.ShipmentSet:
      return (
        <span className="tw-inline-flex tw-h-7 tw-w-7 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-violet-100 tw-text-violet-700">
          <Truck {...baseProps} />
        </span>
      );
    case CampaignActivityAction.ContentUrlSet:
      return (
        <span className="tw-inline-flex tw-h-7 tw-w-7 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-emerald-100 tw-text-emerald-700">
          <Send {...baseProps} />
        </span>
      );
    case CampaignActivityAction.TagsSet:
      return (
        <span className="tw-inline-flex tw-h-7 tw-w-7 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-accent tw-text-accent-foreground">
          <Tag {...baseProps} />
        </span>
      );
    case CampaignActivityAction.ProductSelectionSet:
      return (
        <span className="tw-inline-flex tw-h-7 tw-w-7 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-primary/10 tw-text-primary">
          <Gift {...baseProps} />
        </span>
      );
    case CampaignActivityAction.RejectedSet:
    case CampaignActivityAction.Unrejected:
      return (
        <span className="tw-inline-flex tw-h-7 tw-w-7 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-rose-100 tw-text-rose-700">
          <StickyNote {...baseProps} />
        </span>
      );
    case CampaignActivityAction.SettlementMarked:
    case CampaignActivityAction.SettlementUnmarked:
    case CampaignActivityAction.SettlementMetaUpdated:
      return (
        <span className="tw-inline-flex tw-h-7 tw-w-7 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-amber-100 tw-text-amber-800">
          <Package {...baseProps} />
        </span>
      );
    default:
      return (
        <span className="tw-inline-flex tw-h-7 tw-w-7 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-muted tw-text-muted-foreground">
          <GitBranch {...baseProps} />
        </span>
      );
  }
}

function entryHeadline(entry: CampaignActivityLog): string {
  if (entry.kind === CampaignActivityKind.Comment) return "commented";
  if (entry.action) return ACTION_LABELS[entry.action] ?? entry.action;
  return "performed action";
}

export function ActivityTab({ campaignId }: ActivityTabProps) {
  const timelineQuery = useQuery(
    CAMPAIGN_ACTIVITY_QUERIES.campaignTimeline(campaignId),
  );

  return (
    <div className="tw-mx-auto tw-w-full tw-max-w-[880px] tw-px-8 tw-py-6">
      <div className="tw-rounded-lg tw-border tw-border-border tw-bg-card">
        <div className="tw-border-b tw-border-border tw-px-4 tw-py-3">
          <h3 className="tw-text-[13px] tw-font-semibold">Campaign timeline</h3>
          <p className="tw-text-[11px] tw-text-muted-foreground">
            Stage transitions, shipments, drafts, settlements, and comments
            across every creator.
          </p>
        </div>
        <div className="tw-divide-y tw-divide-border">
          {timelineQuery.isLoading && (
            <div className="tw-px-4 tw-py-6 tw-text-center tw-text-sm tw-text-muted-foreground">
              Loading timeline…
            </div>
          )}
          {timelineQuery.isError && (
            <div className="tw-px-4 tw-py-6 tw-text-center tw-text-sm tw-text-red-600">
              Failed to load timeline.
            </div>
          )}
          {timelineQuery.data?.length === 0 && (
            <div className="tw-px-4 tw-py-12 tw-text-center tw-text-sm tw-text-muted-foreground">
              No activity yet — actions on creator cards will appear here.
            </div>
          )}
          {timelineQuery.data?.map((entry) => (
            <div key={entry.logId} className="tw-flex tw-gap-3 tw-px-4 tw-py-3">
              <ActivityIcon entry={entry} />
              <div className="tw-min-w-0 tw-flex-1">
                <div className="tw-flex tw-flex-wrap tw-items-baseline tw-gap-x-1.5 tw-text-[12px] tw-text-muted-foreground">
                  <span className="tw-font-medium tw-text-foreground">
                    {getActivityAuthorLabel(entry)}
                  </span>
                  <span>{entryHeadline(entry)}</span>
                  <span>·</span>
                  <span>
                    {entry.influencerId
                      ? entry.influencerId.slice(0, 8)
                      : "campaign"}
                  </span>
                  <span>·</span>
                  <span>{formatDate(entry.createdAt)}</span>
                </div>
                {entry.body && (
                  <div
                    className={cn(
                      "tw-mt-1 tw-rounded-md tw-text-[13px] tw-leading-relaxed tw-text-foreground/90",
                      entry.kind === CampaignActivityKind.Comment
                        ? "tw-border tw-border-blue-200 tw-bg-blue-50 tw-px-3 tw-py-2"
                        : "",
                    )}
                  >
                    {entry.body}
                  </div>
                )}
                {entry.fromStage && entry.toStage && (
                  <div className="tw-mt-1 tw-inline-flex tw-items-center tw-gap-1.5 tw-text-[11px] tw-text-muted-foreground">
                    <span className="tw-rounded tw-bg-muted tw-px-1.5 tw-py-0.5">
                      {entry.fromStage}
                    </span>
                    <ArrowRight className="tw-h-3 tw-w-3" />
                    <span className="tw-rounded tw-bg-blue-100 tw-px-1.5 tw-py-0.5 tw-font-semibold tw-text-blue-700">
                      {entry.toStage}
                    </span>
                    {getStageTransitionCauseLabel(entry) && (
                      <>
                        <span>·</span>
                        <span>{getStageTransitionCauseLabel(entry)}</span>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="tw-mt-3 tw-rounded-md tw-border tw-border-dashed tw-border-border tw-p-3 tw-text-[12px] tw-text-muted-foreground">
        Comments + internal notes composer ships in v1.1 — open a creator from
        the pipeline board to comment on them today.
      </div>
    </div>
  );
}
